import { useText } from "@/lib/content";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { FieldError } from "@/components/form/ConfigField";
import { PasswordInput } from "@/components/form/PasswordInput";
import { Notice } from "@/components/form/FormFeedback";
import { usePageTitle } from "@/components/layout/AppLayout";
import { MSG } from "@/lib/validators";

export const AuthCard = ({ title, intro, children }) => (
    <div className="edi-plainbg edi-fill edi-auth-area" data-testid="auth-dotgrid">
    <div className="w-full max-w-md px-4 sm:px-6 py-8">
        <div className="edi-card p-6 sm:p-8" data-testid="auth-card">
            <h1 className="text-3xl font-extrabold">{title}</h1>
            {intro && <p className="mt-2 text-edi-muted">{intro}</p>}
            <div className="mt-6">{children}</div>
        </div>
    </div>
    </div>
);

export const TextInput = ({ id, label, error, type, ...props }) => {
    const Tag = type === "password" ? PasswordInput : "input";
    return (
    <div>
        <label htmlFor={id} className="edi-label">{label}</label>
        <Tag id={id} type={type} className="edi-input" aria-invalid={error ? "true" : "false"} aria-describedby={error ? `${id}-error` : undefined} data-testid={`${id}-input`} {...props} />
        <FieldError id={`${id}-error`} error={error} />
    </div>
    );
};

export default function Login() {
    const t = useText();
    usePageTitle("Accedi");
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user && !busy) return <Navigate to={homeForRole(user.role)} replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!email.trim()) errs.email = MSG.required;
        if (!password) errs.password = MSG.required;
        setErrors(errs);
        setFormError("");
        if (Object.keys(errs).length) return;
        setBusy(true);
        try {
            const u = await login(email, password);
            const from = location.state?.from;
            navigate(from && u.role === "azienda" && !from.startsWith("/amministrazione") ? from : homeForRole(u.role), { replace: true });
        } catch (err) {
            setFormError(errorMessage(err, "Accesso non riuscito, riprova"));
            setBusy(false);
        }
    };

    return (
        <AuthCard title={t("auth.login_title")} intro={t("auth.login_intro")}>
            <form noValidate onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                {formError && <Notice kind="error" role="alert" testId="login-error">{formError}</Notice>}
                <TextInput id="login-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} />
                <TextInput id="login-password" label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errors.password} />
                <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Accesso in corso…" : "Accedi"}</button>
                <div className="flex flex-col gap-2 text-sm">
                    <Link to="/password-dimenticata" data-testid="login-forgot-link">Password dimenticata?</Link>
                    <p>Non hai un account? <Link to="/registrazione" data-testid="login-register-link">Registra la tua azienda</Link></p>
                </div>
            </form>
        </AuthCard>
    );
}

export const useResend = () => {
    const [msg, setMsg] = useState("");
    const send = async () => {
        try {
            const { data } = await api.post("/auth/resend-confirmation");
            setMsg(data.message);
        } catch (err) {
            setMsg(errorMessage(err));
        }
    };
    return { msg, send };
};
